import { FormEvent, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { CodedStatusBadge } from '../../components/ui/CodedStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FieldLabel } from '../../components/ui/FieldLabel';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { listSalesProducts, SalesProductSummary } from '../../services/salesProductsApi';
import { listCompanies } from '../../services/coreCompaniesApi';
import { listProspects } from '../../services/prospectsApi';
import { listSalesOpportunities } from '../../services/salesOpportunitiesApi';
import {
  PricingFeature,
  PricingFeatureKind,
  PricingFeatureUpsert,
  PricingQuote,
  PricingQuoteStatus,
  PricingSettings,
  PricingSimulateLine,
  PricingSimulationResult,
  createPricingFeature,
  createPricingQuote,
  deletePricingFeature,
  getPricingSettings,
  listPricingFeatures,
  listPricingQuotes,
  simulatePricing,
  updatePricingFeature,
  updatePricingQuoteStatus,
  updatePricingSettings,
} from '../../services/salesPricingApi';
import { nextSortOrder } from '../../utils/sortOrderList';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { pricingFieldHelp } from './pricingFieldHelp';
import {
  isPricingFeatureCodeInput,
  isValidPricingFeatureCode,
  PRICING_FEATURE_CODE_MAX_LENGTH,
} from './pricingFeatureCode';

type TabId = 'catalog' | 'simulator' | 'quotes';

const FEATURE_KIND_OPTIONS: { value: PricingFeatureKind; label: string }[] = [
  { value: 'BooleanFlag', label: 'Flag (Sim/Não)' },
  { value: 'Counter', label: 'Contador' },
  { value: 'CounterProgressive', label: 'Contador c/ desconto progressivo' },
];

const QUOTE_STATUS_OPTIONS: { value: PricingQuoteStatus; label: string }[] = [
  { value: 'Draft', label: 'Rascunho' },
  { value: 'Sent', label: 'Enviada' },
  { value: 'Accepted', label: 'Aceita' },
  { value: 'Rejected', label: 'Recusada' },
  { value: 'Archived', label: 'Arquivada' },
];

type FeatureFormState = Omit<PricingFeatureUpsert, 'sortOrder'>;

const emptyFeatureForm: FeatureFormState = {
  salesProductId: null,
  name: '',
  code: '',
  description: '',
  featureKind: 'BooleanFlag',
  billingType: 'Monthly',
  costSetup: 0,
  costUnit: 0,
  includedQuantity: 0,
  hasVolumeDiscount: false,
  notes: '',
  isActive: true,
  discountTiers: [],
};

function money(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function featureKindLabel(value: string): string {
  return FEATURE_KIND_OPTIONS.find((item) => item.value === value)?.label ?? value;
}

export function PricingPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('sales.pricing');
  const [tab, setTab] = useState<TabId>('catalog');

  const [products, setProducts] = useState<SalesProductSummary[]>([]);
  const [settings, setSettings] = useState<PricingSettings | null>(null);
  const [marginDefault, setMarginDefault] = useState(30);
  const [taxDefault, setTaxDefault] = useState(0);

  const [features, setFeatures] = useState<PricingFeature[]>([]);
  const [featureTotal, setFeatureTotal] = useState(0);
  const [featurePage, setFeaturePage] = useState(1);
  const [featureLoading, setFeatureLoading] = useState(true);
  const [editingFeatureId, setEditingFeatureId] = useState<string | null>(null);
  const [featureForm, setFeatureForm] = useState<FeatureFormState>(emptyFeatureForm);
  const [featureSaving, setFeatureSaving] = useState(false);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();

  const [simProductId, setSimProductId] = useState('');
  const [simMargin, setSimMargin] = useState(30);
  const [simTax, setSimTax] = useState(0);
  const [simDesiredProfit, setSimDesiredProfit] = useState('');
  const [simMonths, setSimMonths] = useState(12);
  const [simLines, setSimLines] = useState<Record<string, { enabled: boolean; quantity: number }>>(
    {},
  );
  const [simulation, setSimulation] = useState<PricingSimulationResult | null>(null);
  const [simulating, setSimulating] = useState(false);
  const [quoteTitle, setQuoteTitle] = useState('');
  const [quoteCompanyId, setQuoteCompanyId] = useState('');
  const [quoteProspectId, setQuoteProspectId] = useState('');
  const [quoteOpportunityId, setQuoteOpportunityId] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');
  const [savingQuote, setSavingQuote] = useState(false);

  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [prospects, setProspects] = useState<{ id: string; name: string }[]>([]);
  const [opportunities, setOpportunities] = useState<{ id: string; title: string }[]>([]);

  const [quotes, setQuotes] = useState<PricingQuote[]>([]);
  const [quoteTotal, setQuoteTotal] = useState(0);
  const [quotePage, setQuotePage] = useState(1);
  const [quotesLoading, setQuotesLoading] = useState(false);

  const featurePageSize = 100;
  const quotePageSize = 20;

  async function loadBootstrap(): Promise<void> {
    try {
      const [productResult, settingsResult] = await Promise.all([
        listSalesProducts({ activeOnly: true, page: 1, pageSize: 200 }),
        getPricingSettings(),
      ]);
      setProducts(productResult.items);
      setSettings(settingsResult);
      setMarginDefault(settingsResult.defaultMarginPercent);
      setTaxDefault(settingsResult.defaultTaxRatePercent);
      setSimMargin(settingsResult.defaultMarginPercent);
      setSimTax(settingsResult.defaultTaxRatePercent);
    } catch {
      toast.error('Falha ao carregar dados base da precificação.');
    }
  }

  async function loadFeatures(page = featurePage): Promise<void> {
    setFeatureLoading(true);
    try {
      const result = await listPricingFeatures({ page, pageSize: featurePageSize, activeOnly: false });
      setFeatures(result.items);
      setFeatureTotal(result.totalCount);
      setFeaturePage(result.page);
    } catch {
      toast.error('Falha ao carregar catálogo de precificação.');
    } finally {
      setFeatureLoading(false);
    }
  }

  async function loadQuotes(page = quotePage): Promise<void> {
    setQuotesLoading(true);
    try {
      const result = await listPricingQuotes({ page, pageSize: quotePageSize });
      setQuotes(result.items);
      setQuoteTotal(result.totalCount);
      setQuotePage(result.page);
    } catch {
      toast.error('Falha ao carregar cotações.');
    } finally {
      setQuotesLoading(false);
    }
  }

  async function loadLinkOptions(): Promise<void> {
    try {
      const [companyResult, prospectResult, opportunityResult] = await Promise.all([
        listCompanies({ page: 1, pageSize: 200 }),
        listProspects({ page: 1, pageSize: 200 }),
        listSalesOpportunities(),
      ]);
      setCompanies(
        (companyResult.items ?? []).map((item) => ({
          id: item.id,
          name: item.name,
        })),
      );
      setProspects(
        (prospectResult.items ?? []).map((item) => ({
          id: item.id,
          name: item.fullName || item.companyName || item.id,
        })),
      );
      setOpportunities(
        (opportunityResult.items ?? []).map((item) => ({
          id: item.id,
          title: item.title,
        })),
      );
    } catch {
      // vínculos opcionais — não bloqueia o simulador
    }
  }

  useEffect(() => {
    void loadBootstrap();
    void loadFeatures(1);
    // Bootstrap + catálogo só no mount; paginação chama loadFeatures explicitamente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === 'quotes') void loadQuotes(quotePage);
    if (tab === 'simulator') void loadLinkOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const catalogForSimulation = useMemo(() => {
    return features.filter(
      (feature) =>
        feature.isActive &&
        (!simProductId || !feature.salesProductId || feature.salesProductId === simProductId),
    );
  }, [features, simProductId]);

  useEffect(() => {
    setSimLines((current) => {
      const next: Record<string, { enabled: boolean; quantity: number }> = {};
      for (const feature of catalogForSimulation) {
        next[feature.id] = current[feature.id] ?? {
          enabled: feature.featureKind === 'BooleanFlag' ? false : true,
          quantity: feature.featureKind === 'BooleanFlag' ? 1 : Math.max(1, feature.includedQuantity || 1),
        };
      }
      return next;
    });
  }, [catalogForSimulation]);

  function resetFeatureForm(): void {
    setEditingFeatureId(null);
    setFeatureForm(emptyFeatureForm);
  }

  function startEditFeature(feature: PricingFeature): void {
    expandForm();
    setEditingFeatureId(feature.id);
    setFeatureForm({
      salesProductId: feature.salesProductId ?? null,
      name: feature.name,
      code: feature.code,
      description: feature.description,
      featureKind: feature.featureKind as PricingFeatureKind,
      billingType: feature.billingType,
      costSetup: feature.costSetup,
      costUnit: feature.costUnit,
      includedQuantity: feature.includedQuantity,
      hasVolumeDiscount: feature.hasVolumeDiscount,
      notes: feature.notes,
      isActive: feature.isActive,
      discountTiers: feature.discountTiers ?? [],
    });
  }

  async function handleSaveSettings(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;
    try {
      const updated = await updatePricingSettings({
        defaultMarginPercent: marginDefault,
        defaultTaxRatePercent: taxDefault,
        currency: settings?.currency ?? 'BRL',
      });
      setSettings(updated);
      toast.success('Parâmetros padrão salvos.');
    } catch {
      toast.error('Falha ao salvar parâmetros.');
    }
  }

  async function handleSaveFeature(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const name = featureForm.name.trim();
    const code = featureForm.code.trim();
    const description = featureForm.description.trim();
    if (!name) {
      toast.error('Informe o nome do item.');
      return;
    }
    if (!isValidPricingFeatureCode(code)) {
      toast.error('Informe um código com letras minúsculas, números e hífen, sem espaços.');
      return;
    }
    if (!description) {
      toast.error('Informe a descrição do item.');
      return;
    }

    setFeatureSaving(true);
    try {
      const payload: PricingFeatureUpsert = {
        ...featureForm,
        name,
        code,
        description,
        sortOrder: editingFeatureId
          ? (features.find((item) => item.id === editingFeatureId)?.sortOrder ?? 0)
          : nextSortOrder(features),
      };
      if (editingFeatureId) {
        await updatePricingFeature(editingFeatureId, payload);
        toast.success('Item atualizado.');
        collapseForm(resetFeatureForm);
      } else {
        await createPricingFeature(payload);
        toast.success('Item criado.');
        handleCreateSuccess(resetFeatureForm);
      }
      await loadFeatures(featurePage);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar item de precificação.'));
    } finally {
      setFeatureSaving(false);
    }
  }

  function handleCodeChange(rawValue: string): void {
    const next = rawValue.toLowerCase();
    if (!isPricingFeatureCodeInput(next)) {
      if (rawValue.length - featureForm.code.length > 1) {
        toast.error(
          'O código aceita apenas letras minúsculas, números e hífen. Espaços e acentos não são permitidos.',
        );
      }
      return;
    }

    setFeatureForm((current) => ({ ...current, code: next }));
  }

  async function handleDeleteFeature(feature: PricingFeature): Promise<void> {
    if (!canWrite) return;
    if (
      !(await confirm({
        title: 'Excluir item',
        message: `Excluir "${feature.name}" do catálogo de precificação?`,
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }
    try {
      await deletePricingFeature(feature.id);
      toast.success('Item excluído.');
      await loadFeatures(featurePage);
    } catch {
      toast.error('Falha ao excluir item.');
    }
  }

  async function handleSimulate(): Promise<void> {
    setSimulating(true);
    try {
      const lines: PricingSimulateLine[] = catalogForSimulation.map((feature) => ({
        pricingFeatureId: feature.id,
        enabled: simLines[feature.id]?.enabled ?? false,
        quantity: simLines[feature.id]?.quantity ?? 0,
      }));
      const desired =
        simDesiredProfit.trim() === '' ? null : Number(simDesiredProfit.replace(',', '.'));
      const result = await simulatePricing({
        salesProductId: simProductId || null,
        contractMonths: simMonths,
        marginPercent: desired != null && !Number.isNaN(desired) ? null : simMargin,
        taxRatePercent: simTax,
        desiredProfitAmount: desired != null && !Number.isNaN(desired) ? desired : null,
        lines,
        professionalLines: [],
      });
      setSimulation(result);
      toast.success('Simulação atualizada.');
    } catch {
      toast.error('Falha ao simular precificação.');
    } finally {
      setSimulating(false);
    }
  }

  async function handleSaveQuote(): Promise<void> {
    if (!canWrite) return;
    if (!quoteTitle.trim()) {
      toast.error('Informe um título para a cotação.');
      return;
    }
    setSavingQuote(true);
    try {
      const lines: PricingSimulateLine[] = catalogForSimulation.map((feature) => ({
        pricingFeatureId: feature.id,
        enabled: simLines[feature.id]?.enabled ?? false,
        quantity: simLines[feature.id]?.quantity ?? 0,
      }));
      const desired =
        simDesiredProfit.trim() === '' ? null : Number(simDesiredProfit.replace(',', '.'));
      const quote = await createPricingQuote({
        title: quoteTitle.trim(),
        salesProductId: simProductId || null,
        companyId: quoteCompanyId || null,
        prospectId: quoteProspectId || null,
        salesOpportunityId: quoteOpportunityId || null,
        contractMonths: simMonths,
        marginPercent: desired != null && !Number.isNaN(desired) ? null : simMargin,
        taxRatePercent: simTax,
        desiredProfitAmount: desired != null && !Number.isNaN(desired) ? desired : null,
        notes: quoteNotes,
        lines,
        professionalLines: [],
      });
      toast.success(`Cotação ${quote.quoteNumber} salva.`);
      setTab('quotes');
      await loadQuotes(1);
    } catch {
      toast.error('Falha ao salvar cotação.');
    } finally {
      setSavingQuote(false);
    }
  }

  async function handleQuoteStatus(quote: PricingQuote, status: PricingQuoteStatus): Promise<void> {
    if (!canWrite) return;
    try {
      await updatePricingQuoteStatus(quote.id, status);
      toast.success('Status atualizado.');
      await loadQuotes(quotePage);
    } catch {
      toast.error('Falha ao atualizar status.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Precificação"
        description="Catálogo de custos por feature, simulação com margem/impostos e histórico de cotações."
      />

      <div className="erp-btn-actions mb-4">
        {(
          [
            ['catalog', 'Catálogo'],
            ['simulator', 'Simulador'],
            ['quotes', 'Cotações'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={tab === id ? 'erp-btn-primary' : 'erp-btn-secondary'}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'catalog' ? (
        <>
          <form onSubmit={(event) => void handleSaveSettings(event)} className="erp-form mb-4">
            <div className="erp-form-grid">
              <label className="erp-label">
                <FieldLabel {...pricingFieldHelp.marginDefault}>Margem padrão (%)</FieldLabel>
                <input
                  type="number"
                  className="erp-input"
                  value={marginDefault}
                  onChange={(event) => setMarginDefault(Number(event.target.value))}
                  disabled={!canWrite}
                  step="0.01"
                />
              </label>
              <label className="erp-label">
                <FieldLabel {...pricingFieldHelp.taxDefault}>Impostos padrão (%)</FieldLabel>
                <input
                  type="number"
                  className="erp-input"
                  value={taxDefault}
                  onChange={(event) => setTaxDefault(Number(event.target.value))}
                  disabled={!canWrite}
                  step="0.01"
                />
              </label>
            </div>
            {canWrite ? (
              <FormActionBar>
                <button type="submit" className="erp-btn-secondary">
                  Salvar parâmetros
                </button>
              </FormActionBar>
            ) : null}
          </form>

          {canWrite ? (
            <CollapsibleFormSection
              title={editingFeatureId ? 'Editar item' : 'Novo item de precificação'}
              expanded={formExpanded}
              onToggle={() => toggleOrCancel(resetFeatureForm)}
              editingSummary={
                editingFeatureId
                  ? `Editando ${featureForm.name || 'item'} — clique para expandir`
                  : undefined
              }
              createSummary="Clique para cadastrar custo de feature/volumetria"
              isEditing={!!editingFeatureId}
            >
              <form
                onSubmit={(event) => void handleSaveFeature(event)}
                className="erp-form px-4 pb-4 pt-3"
              >
                <div className="erp-form-grid">
                  <label className="erp-label">
                    <FieldLabel {...pricingFieldHelp.name} required>
                      Nome
                    </FieldLabel>
                    <input
                      className="erp-input"
                      required
                      maxLength={200}
                      value={featureForm.name}
                      onChange={(event) =>
                        setFeatureForm((current) => ({ ...current, name: event.target.value }))
                      }
                    />
                  </label>
                  <label className="erp-label">
                    <FieldLabel {...pricingFieldHelp.code} required>
                      Código
                    </FieldLabel>
                    <input
                      className="erp-input font-mono"
                      required
                      spellCheck={false}
                      autoComplete="off"
                      autoCapitalize="none"
                      maxLength={PRICING_FEATURE_CODE_MAX_LENGTH}
                      pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                      title="Use letras minúsculas, números e hífen. Sem espaços nem acentos."
                      value={featureForm.code}
                      onChange={(event) => handleCodeChange(event.target.value)}
                    />
                  </label>
                  <label className="erp-label erp-form-grid-full">
                    <FieldLabel {...pricingFieldHelp.description} required>
                      Descrição
                    </FieldLabel>
                    <textarea
                      className="erp-input"
                      required
                      rows={2}
                      maxLength={4000}
                      value={featureForm.description}
                      onChange={(event) =>
                        setFeatureForm((current) => ({
                          ...current,
                          description: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="erp-label">
                    <FieldLabel {...pricingFieldHelp.product}>Produto (opcional)</FieldLabel>
                    <select
                      className="erp-input"
                      value={featureForm.salesProductId ?? ''}
                      onChange={(event) =>
                        setFeatureForm((current) => ({
                          ...current,
                          salesProductId: event.target.value || null,
                        }))
                      }
                    >
                      <option value="">Todos os produtos</option>
                      {products.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="erp-label">
                    <FieldLabel {...pricingFieldHelp.kind} required>
                      Tipo
                    </FieldLabel>
                    <select
                      className="erp-input"
                      required
                      value={featureForm.featureKind}
                      onChange={(event) =>
                        setFeatureForm((current) => ({
                          ...current,
                          featureKind: event.target.value as PricingFeatureKind,
                          hasVolumeDiscount: event.target.value === 'CounterProgressive',
                        }))
                      }
                    >
                      {FEATURE_KIND_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="erp-label">
                    <FieldLabel {...pricingFieldHelp.costSetup} required>
                      Custo setup (R$)
                    </FieldLabel>
                    <input
                      type="number"
                      className="erp-input"
                      required
                      min={0}
                      step="0.01"
                      value={featureForm.costSetup}
                      onChange={(event) =>
                        setFeatureForm((current) => ({
                          ...current,
                          costSetup: Number(event.target.value),
                        }))
                      }
                    />
                  </label>
                  <label className="erp-label">
                    <FieldLabel {...pricingFieldHelp.costUnit} required>
                      Custo unitário / MRR (R$)
                    </FieldLabel>
                    <input
                      type="number"
                      className="erp-input"
                      required
                      min={0}
                      step="0.01"
                      value={featureForm.costUnit}
                      onChange={(event) =>
                        setFeatureForm((current) => ({
                          ...current,
                          costUnit: Number(event.target.value),
                        }))
                      }
                    />
                  </label>
                  <label className="erp-label">
                    <FieldLabel {...pricingFieldHelp.includedQuantity} required>
                      Qtd. inclusa
                    </FieldLabel>
                    <input
                      type="number"
                      className="erp-input"
                      required
                      min={0}
                      step="1"
                      value={featureForm.includedQuantity}
                      onChange={(event) =>
                        setFeatureForm((current) => ({
                          ...current,
                          includedQuantity: Number(event.target.value),
                        }))
                      }
                    />
                  </label>
                </div>
                <FormActionBar onCancel={() => toggleOrCancel(resetFeatureForm)}>
                  <button type="submit" className="erp-btn-primary" disabled={featureSaving}>
                    {featureSaving ? 'Salvando...' : 'Salvar'}
                  </button>
                </FormActionBar>
              </form>
            </CollapsibleFormSection>
          ) : null}

          <div className="erp-table-wrap mt-4">
            {featureLoading ? (
              <p className="erp-loading">Carregando...</p>
            ) : features.length === 0 ? (
              <p className="erp-empty">Nenhum item no catálogo.</p>
            ) : (
              <div className="erp-table-scroll">
                <table className="erp-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Tipo</th>
                      <th>Setup</th>
                      <th>Unitário</th>
                      <th>Produto</th>
                      <th>Status</th>
                      {canWrite ? <th>Ações</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((feature) => (
                      <tr key={feature.id}>
                        <td>{feature.name}</td>
                        <td>{featureKindLabel(feature.featureKind)}</td>
                        <td>{money(feature.costSetup)}</td>
                        <td>{money(feature.costUnit)}</td>
                        <td>
                          {products.find((item) => item.id === feature.salesProductId)?.name ??
                            'Todos'}
                        </td>
                        <td>
                          <ActiveStatusBadge active={feature.isActive} />
                        </td>
                        {canWrite ? (
                          <td>
                            <div className="erp-btn-actions">
                              <EditIconButton onClick={() => startEditFeature(feature)} />
                              <DeleteIconButton onClick={() => void handleDeleteFeature(feature)} />
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <ListPaginationBar
              totalCount={featureTotal}
              page={featurePage}
              pageSize={featurePageSize}
              onPrevious={() => void loadFeatures(Math.max(1, featurePage - 1))}
              onNext={() => void loadFeatures(featurePage + 1)}
            />
          </div>
        </>
      ) : null}

      {tab === 'simulator' ? (
        <div className="space-y-4">
          <div className="erp-form-grid">
            <label className="erp-label">
              <FieldLabel {...pricingFieldHelp.simProduct}>Produto</FieldLabel>
              <select
                className="erp-input"
                value={simProductId}
                onChange={(event) => setSimProductId(event.target.value)}
              >
                <option value="">Todos / genérico</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              <FieldLabel {...pricingFieldHelp.simMargin}>Margem (%)</FieldLabel>
              <input
                type="number"
                className="erp-input"
                step="0.01"
                value={simMargin}
                onChange={(event) => setSimMargin(Number(event.target.value))}
                disabled={simDesiredProfit.trim() !== ''}
              />
            </label>
            <label className="erp-label">
              <FieldLabel {...pricingFieldHelp.simTax}>Impostos (%)</FieldLabel>
              <input
                type="number"
                className="erp-input"
                step="0.01"
                value={simTax}
                onChange={(event) => setSimTax(Number(event.target.value))}
              />
            </label>
            <label className="erp-label">
              <FieldLabel {...pricingFieldHelp.simDesiredProfit}>
                Lucro desejado (R$) — simulação reversa
              </FieldLabel>
              <input
                className="erp-input"
                value={simDesiredProfit}
                placeholder="Opcional"
                onChange={(event) => setSimDesiredProfit(event.target.value)}
              />
            </label>
            <label className="erp-label">
              <FieldLabel {...pricingFieldHelp.simMonths}>Vigência (meses)</FieldLabel>
              <input
                type="number"
                className="erp-input"
                min={1}
                value={simMonths}
                onChange={(event) => setSimMonths(Number(event.target.value))}
              />
            </label>
          </div>

          <div className="erp-table-wrap">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Incluir</th>
                  <th>Feature</th>
                  <th>Qtd.</th>
                  <th>Custo setup</th>
                  <th>Custo MRR</th>
                </tr>
              </thead>
              <tbody>
                {catalogForSimulation.map((feature) => {
                  const line = simLines[feature.id];
                  return (
                    <tr key={feature.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={line?.enabled ?? false}
                          onChange={(event) =>
                            setSimLines((current) => ({
                              ...current,
                              [feature.id]: {
                                enabled: event.target.checked,
                                quantity: current[feature.id]?.quantity ?? 1,
                              },
                            }))
                          }
                        />
                      </td>
                      <td>
                        <div className="font-medium">{feature.name}</div>
                        <div className="text-xs text-secondary-500">
                          {featureKindLabel(feature.featureKind)}
                        </div>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="erp-input w-28"
                          disabled={feature.featureKind === 'BooleanFlag'}
                          value={line?.quantity ?? 0}
                          onChange={(event) =>
                            setSimLines((current) => ({
                              ...current,
                              [feature.id]: {
                                enabled: current[feature.id]?.enabled ?? true,
                                quantity: Number(event.target.value),
                              },
                            }))
                          }
                        />
                      </td>
                      <td>{money(feature.costSetup)}</td>
                      <td>{money(feature.costUnit)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            className="erp-btn-primary"
            disabled={simulating}
            onClick={() => void handleSimulate()}
          >
            {simulating ? 'Calculando...' : 'Calcular simulação'}
          </button>

          {simulation ? (
            <div className="erp-form space-y-3 rounded border border-secondary-200 p-4">
              <h3 className="text-lg font-semibold">Resultado</h3>
              <div className="erp-form-grid">
                <div>
                  <div className="text-sm text-secondary-500">Custo setup</div>
                  <div className="font-medium">{money(simulation.totals.costSetupTotal)}</div>
                </div>
                <div>
                  <div className="text-sm text-secondary-500">Custo MRR</div>
                  <div className="font-medium">{money(simulation.totals.costMrrTotal)}</div>
                </div>
                <div>
                  <div className="text-sm text-secondary-500">Preço setup</div>
                  <div className="font-medium">{money(simulation.totals.sellSetupTotal)}</div>
                </div>
                <div>
                  <div className="text-sm text-secondary-500">Preço MRR</div>
                  <div className="font-medium">{money(simulation.totals.sellMrrTotal)}</div>
                </div>
                <div>
                  <div className="text-sm text-secondary-500">Lucro setup</div>
                  <div className="font-medium">{money(simulation.totals.profitAmountSetup)}</div>
                </div>
                <div>
                  <div className="text-sm text-secondary-500">Lucro MRR</div>
                  <div className="font-medium">{money(simulation.totals.profitAmountMrr)}</div>
                </div>
                <div>
                  <div className="text-sm text-secondary-500">Impostos (setup + MRR)</div>
                  <div className="font-medium">
                    {money(simulation.totals.taxAmountSetup + simulation.totals.taxAmountMrr)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-secondary-500">Total no contrato</div>
                  <div className="font-medium">{money(simulation.totals.contractTotal)}</div>
                </div>
                <div>
                  <div className="text-sm text-secondary-500">Margem aplicada</div>
                  <div className="font-medium">{simulation.marginPercent.toFixed(2)}%</div>
                </div>
              </div>

              {canWrite ? (
                <>
                  <h4 className="pt-2 font-semibold">Salvar cotação</h4>
                  <div className="erp-form-grid">
                    <label className="erp-label">
                      <FieldLabel {...pricingFieldHelp.quoteTitle}>Título</FieldLabel>
                      <input
                        className="erp-input"
                        value={quoteTitle}
                        onChange={(event) => setQuoteTitle(event.target.value)}
                      />
                    </label>
                    <label className="erp-label">
                      <FieldLabel {...pricingFieldHelp.quoteCompany}>Empresa</FieldLabel>
                      <select
                        className="erp-input"
                        value={quoteCompanyId}
                        onChange={(event) => setQuoteCompanyId(event.target.value)}
                      >
                        <option value="">—</option>
                        {companies.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="erp-label">
                      <FieldLabel {...pricingFieldHelp.quoteProspect}>Prospect / lead</FieldLabel>
                      <select
                        className="erp-input"
                        value={quoteProspectId}
                        onChange={(event) => setQuoteProspectId(event.target.value)}
                      >
                        <option value="">—</option>
                        {prospects.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="erp-label">
                      <FieldLabel {...pricingFieldHelp.quoteOpportunity}>Oportunidade</FieldLabel>
                      <select
                        className="erp-input"
                        value={quoteOpportunityId}
                        onChange={(event) => setQuoteOpportunityId(event.target.value)}
                      >
                        <option value="">—</option>
                        {opportunities.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.title}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="erp-label">
                      <FieldLabel {...pricingFieldHelp.quoteNotes}>Observações</FieldLabel>
                      <input
                        className="erp-input"
                        value={quoteNotes}
                        onChange={(event) => setQuoteNotes(event.target.value)}
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    className="erp-btn-primary"
                    disabled={savingQuote}
                    onClick={() => void handleSaveQuote()}
                  >
                    {savingQuote ? 'Salvando...' : 'Salvar cotação no histórico'}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === 'quotes' ? (
        <div className="erp-table-wrap">
          {quotesLoading ? (
            <p className="erp-loading">Carregando...</p>
          ) : quotes.length === 0 ? (
            <p className="erp-empty">Nenhuma cotação registrada.</p>
          ) : (
            <div className="erp-table-scroll">
              <table className="erp-table">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Título</th>
                    <th>Status</th>
                    <th>Setup</th>
                    <th>MRR</th>
                    <th>Contrato</th>
                    <th>Criada em</th>
                    {canWrite ? <th>Ações</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((quote) => (
                    <tr key={quote.id}>
                      <td>{quote.quoteNumber}</td>
                      <td>{quote.title}</td>
                      <td>
                        <CodedStatusBadge
                          status={quote.status}
                          label={
                            QUOTE_STATUS_OPTIONS.find((item) => item.value === quote.status)?.label ??
                            quote.status
                          }
                        />
                      </td>
                      <td>{money(quote.sellSetupTotal)}</td>
                      <td>{money(quote.sellMrrTotal)}</td>
                      <td>{money(quote.annualTotal)}</td>
                      <td>{new Date(quote.createdAtUtc).toLocaleString('pt-BR')}</td>
                      {canWrite ? (
                        <td>
                          <select
                            className="erp-input"
                            value={quote.status}
                            onChange={(event) =>
                              void handleQuoteStatus(
                                quote,
                                event.target.value as PricingQuoteStatus,
                              )
                            }
                          >
                            {QUOTE_STATUS_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <ListPaginationBar
            totalCount={quoteTotal}
            page={quotePage}
            pageSize={quotePageSize}
            onPrevious={() => void loadQuotes(Math.max(1, quotePage - 1))}
            onNext={() => void loadQuotes(quotePage + 1)}
          />
        </div>
      ) : null}
    </PageShell>
  );
}
